/**
 * App resume recovery: signed URL refresh + CSS health.
 *
 * Signed URLs expire after 1 hour. When the user minimizes the app (web or phone)
 * and reopens after being away for 55+ minutes, triggers a full page reload so
 * the app fetches fresh signed URLs.
 *
 * iOS Safari can also purge stylesheets after long background while JS/React state
 * remains. CSS health is checked on every resume; unhealthy styles trigger reload
 * even when the hidden-duration timestamp was lost.
 *
 * Reload is based on how long the tab was hidden (not total time since first open),
 * so returning after a short break does not reload a tab that has been open for hours.
 *
 * Hidden timestamps use localStorage (not sessionStorage) because iOS often clears
 * sessionStorage on long suspend.
 *
 * Uses Page Visibility API (visibilitychange) and pageshow (bfcache) on mobile and desktop.
 */

import { SIGNED_URL_REFRESH } from '../../constants/common';
import { getLocalItem, removeLocalItem, setLocalItem } from './localStorage';
import { reloadIfCssUnhealthy } from './cssHealth';
import { shouldBlockSignedUrlRefresh } from './paymentFlowGuard';
import { emitSignedUrlRefresh } from '../signedUrlCache';

const SESSION_START_KEY = SIGNED_URL_REFRESH.STORAGE_KEY;
const HIDDEN_AT_KEY = SIGNED_URL_REFRESH.HIDDEN_AT_KEY;
const RELOAD_AFTER_MS = SIGNED_URL_REFRESH.RELOAD_AFTER_MS;

function recordTabHiddenAt() {
  if (typeof window === 'undefined') return;
  try {
    setLocalItem(HIDDEN_AT_KEY, Date.now());
  } catch {
    // Ignore localStorage errors (private mode, etc.)
  }
}

function parseHiddenTimestamp(raw) {
  if (raw == null) return null;
  const ts = typeof raw === 'number' ? raw : parseInt(raw, 10);
  return Number.isFinite(ts) ? ts : null;
}

/**
 * Milliseconds the tab was hidden before this visibility event; 0 if not hidden or unknown.
 * Clears the stored hidden timestamp so it is only consumed once per return.
 * @returns {number}
 */
function consumeHiddenDurationMs() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = getLocalItem(HIDDEN_AT_KEY);
    removeLocalItem(HIDDEN_AT_KEY);
    const hiddenAt = parseHiddenTimestamp(raw);
    if (hiddenAt == null) return 0;
    return Math.max(0, Date.now() - hiddenAt);
  } catch {
    return 0;
  }
}

/**
 * Peek at hidden duration without clearing (for tests / diagnostics).
 * @returns {number|null} ms hidden, or null if tab is not marked hidden
 */
function peekHiddenDurationMs() {
  if (typeof window === 'undefined') return null;
  try {
    const hiddenAt = parseHiddenTimestamp(getLocalItem(HIDDEN_AT_KEY));
    if (hiddenAt == null) return null;
    return Math.max(0, Date.now() - hiddenAt);
  } catch {
    return null;
  }
}

/**
 * Persist the current time as the signed-URL session start.
 * Call on app load (and after reload) so we know when the current URLs were issued.
 */
export function recordSignedUrlSessionStart() {
  if (typeof window === 'undefined') return;
  try {
    setLocalItem(SESSION_START_KEY, Date.now());
  } catch {
    // Ignore localStorage errors (private mode, etc.)
  }
}

/**
 * Returns the stored session start timestamp, or null if missing/invalid.
 * @returns {number|null}
 */
export function getSignedUrlSessionStart() {
  if (typeof window === 'undefined') return null;
  try {
    const value = getLocalItem(SESSION_START_KEY);
    const ts = typeof value === 'number' ? value : parseInt(value, 10);
    return Number.isFinite(ts) ? ts : null;
  } catch {
    return null;
  }
}

/**
 * Returns true if the tab has been hidden for at least RELOAD_AFTER_MS (55 min).
 * @returns {boolean}
 */
export function shouldReloadForSignedUrlRefresh() {
  const hiddenMs = peekHiddenDurationMs();
  if (hiddenMs == null) return false;
  return hiddenMs >= RELOAD_AFTER_MS;
}

/**
 * Runs resume recovery when the page becomes visible:
 * 1. CSS health — reload if styles were purged (always checked on resume).
 * 2. Signed URLs — in-place refresh first; full reload only as fallback for
 *    screens that still bake signed URLs into long-lived Redux/state trees.
 */
export function onVisibilityVisible() {
  if (typeof window === 'undefined') return;

  // CSS reload bypasses payment/call guards — UI is unusable without styles.
  if (reloadIfCssUnhealthy()) {
    return;
  }

  // Keep hidden timestamp until we can act on it (same as pre-refactor behavior).
  if (shouldBlockSignedUrlRefresh()) return;

  const hiddenMs = consumeHiddenDurationMs();

  if (hiddenMs >= RELOAD_AFTER_MS) {
    // Chat (and any listener) refreshes keys → fresh signed URLs without navigation.
    emitSignedUrlRefresh();
    // Feed/profile still replace keys with signed URLs in memory; reload renews those.
    window.location.reload();
    return;
  }

  recordSignedUrlSessionStart();
}

/**
 * If the process was killed while the tab was hidden, localStorage still holds
 * the hidden timestamp. Run recovery once on mount so signed-URL and CSS checks
 * still run after a cold start.
 */
export function recoverFromBackgroundOnMount() {
  if (peekHiddenDurationMs() == null) return;
  onVisibilityVisible();
}

/**
 * Subscribes to visibility changes and triggers reload or session refresh when the page becomes visible.
 * @returns {function()} Unsubscribe (remove listener).
 */
export function subscribeToVisibilityForSignedUrlRefresh() {
  if (typeof document === 'undefined') return () => {};

  const handler = () => {
    if (document.visibilityState === 'hidden') {
      recordTabHiddenAt();
      return;
    }
    if (document.visibilityState === 'visible') {
      onVisibilityVisible();
    }
  };

  document.addEventListener('visibilitychange', handler);

  const onPageShow = (e) => {
    if (!e?.persisted) return;
    onVisibilityVisible();
  };
  window.addEventListener('pageshow', onPageShow);

  return () => {
    document.removeEventListener('visibilitychange', handler);
    window.removeEventListener('pageshow', onPageShow);
  };
}
